import {Store} from "@/models/StoreModel";

type ResultType = {
    color: string;
    key: string;
    label: string;
    inactiveIcon: {
        image: string;
        width: string;
        height: string;
    }
    activeIcon: {
        image: string;
        width: string;
        height: string;
    }
}

type LatLng = {
    'lat': number,
    'lng': number
}

type CountryBounding = {
    'sw': LatLng,
    'ne': LatLng
}

enum ButtonTypes {
    link = 'link',
    function = 'function'
}

type LinkButton = {
    type: ButtonTypes.link;
    href: string;
    cssclass?: string;
    target: string;
    text: string;
}

type FunctionButton = {
    type: ButtonTypes.function;
    cssclass?: string;
    callback: (store: Store) => void
    text: string
}

class ConfigurationComposition {
    private _geoapifyKey: string = '';
    private _geoapifyLang: string = 'de';
    private _geoapifyFilter: string[] = ['de'];
    private _leafletTileSrc: string = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
    private _leafletAttribution: string = '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors';
    private _bounding: CountryBounding = {
        'sw': {'lat': 47.2701114, 'lng': 5.8663153},
        'ne': {'lat': 55.099161, 'lng': 15.0419319}
    };
    private _defaultCountry: string = 'de';
    private _defaultLocation: LatLng = {
        'lat': 51.4467438,
        'lng': 6.6578916
    };
    private _tooltipTemplate: Function = (store: Store) => '';
    private _inactiveMarkerScaleFactor: number = 1;
    private _activeMarkerScaleFactor: number = 1;
    private _resultTypes: ResultType[] = [];
    private _isCenteredMap: boolean = false;
    private _enableFilter: boolean = true;
    private _enableCountryDropdown: boolean = true;
    private _enableLocationSearch: boolean = true;
    private _filterDistances: { key: number, label: number }[] = [];
    private _filterRegions: { key: string, label: string }[] = [];
    private _filterTypes: { key: string, label: string }[] = [];
    private _loadInitially: boolean = false;
    private _enableTooltip: boolean = true;
    private _apiEndpointTemplate: (searchQuery: string, lat: number, lng: number, distance: number, region: string, type: string, country: string) => '' = (searchQuery: string, lat: number, lng: number, distance: number, region: string, type: string, country: string) => '';
    private _buttonListTemplate: (store: Store, route: { origin: string, destination: string }) => (FunctionButton | LinkButton)[] = (store: Store, route: { origin: string, destination: string }) => [];
    private _showPhoneNumber: boolean = true;

    constructor() {
        try {
            this._geoapifyKey = `${window.storefinder.geoapifyKey}`;
            this._geoapifyLang = `${window.storefinder.geoapifyLang}`;
            this._geoapifyFilter = window.storefinder.geoapifyFilter;
            this._leafletTileSrc = `${window.storefinder.leafletTileSrc}`;
            this._leafletAttribution = `${window.storefinder.leafletAttribution}`;
            this._bounding = window.storefinder.bounding;
            this._defaultCountry = window.storefinder.defaultCountry;
            this._defaultLocation = window.storefinder.defaultLocation;
            this._tooltipTemplate = window.storefinder.tooltipTemplate;
            this._inactiveMarkerScaleFactor = window.storefinder.inactiveMarkerScaleFactor;
            this._activeMarkerScaleFactor = window.storefinder.activeMarkerScaleFactor;
            this._resultTypes = window.storefinder.resultTypes;
            this._isCenteredMap = window.storefinder.isCenteredMap;
            this._enableFilter = window.storefinder.enableFilter;
            this._enableCountryDropdown = window.storefinder.enableCountryDropdown;
            this._enableLocationSearch = window.storefinder.enableLocationSearch;
            this._filterDistances = window.storefinder.filterDistances;
            this._filterRegions = window.storefinder.filterRegions;
            this._filterTypes = window.storefinder.filterTypes;
            this._loadInitially = window.storefinder.loadInitially;
            this._enableTooltip = window.storefinder.enableTooltip;
            // @ts-ignore
            this._apiEndpointTemplate = window.storefinder.apiEndpointTemplate;
            // @ts-ignore
            this._buttonListTemplate = window.storefinder.buttonListTemplate;
            this._showPhoneNumber = window.storefinder.showPhoneNumber;

        } catch (e) {
            throw 'please check the runtime configuration'
        }
    }

    get geoapifyKey(): string {
        return this._geoapifyKey;
    }

    get geoapifyLang(): string {
        return this._geoapifyLang;
    }

    get geoapifyFilter(): string[] {
        return this._geoapifyFilter;
    }

    get leafletTileSrc(): string {
        return this._leafletTileSrc;
    }

    get leafletAttribution(): string {
        return this._leafletAttribution;
    }

    get bounding(): CountryBounding {
        return this._bounding;
    }

    get defaultCountry(): string {
        return this._defaultCountry;
    }

    get defaultLocation(): LatLng {
        return this._defaultLocation;
    }

    get tooltipTemplate(): Function {
        return this._tooltipTemplate;
    }

    get inactiveMarkerScaleFactor(): number {
        return this._inactiveMarkerScaleFactor;
    }

    get activeMarkerScaleFactor(): number {
        return this._activeMarkerScaleFactor;
    }

    get resultTypes(): ResultType[] {
        return this._resultTypes;
    }

    get isCenteredMap(): boolean {
        return this._isCenteredMap;
    }

    get enableFilter(): boolean {
        return this._enableFilter;
    }

    get enableCountryDropdown(): boolean {
        return this._enableCountryDropdown;
    }

    get enableLocationSearch(): boolean {
        return this._enableLocationSearch;
    }

    get loadInitially(): boolean {
        return this._loadInitially;
    }

    get enableTooltip(): boolean {
        return this._enableTooltip;
    }

    get apiEndpointTemplate(): Function {
        return this._apiEndpointTemplate;
    }

    get buttonListTemplate(): (store: Store, route: { origin: string; destination: string }) => (FunctionButton | LinkButton)[] {
        return this._buttonListTemplate;
    }

    get filterDistances(): { key: number; label: number }[] {
        return this._filterDistances;
    }

    get filterRegions(): { key: string; label: string }[] {
        return this._filterRegions;
    }

    get filterTypes(): { key: string; label: string }[] {
        return this._filterTypes;
    }

    get showPhoneNumber(): boolean {
        return this._showPhoneNumber;
    }
}

export const configuration = new ConfigurationComposition()
